/**
 * THE AUTHORED MASTER, LOADED — the implementation `authoredMasterSeam.ts`
 * deliberately withheld until the contract branch landed.
 *
 * It has landed. `authoredSkeletonContract.ts` carries the 74-bone table,
 * `skeletonValidation.ts` the shared checker, `productionAssetManifest.ts` the
 * frozen bytes of the four merged LODs and their shared atlas, and
 * `productionAnimationManifest.ts` the clip admission keyed on the FILE STEM.
 * This module is the loader that ties them to a `PlayerVisualAsset`, and it adds
 * no judgement of its own: every refusal below is either the seam's or the
 * integrity layer's, invoked, not restated.
 *
 * ── Why there is a preload step ────────────────────────────────────────────
 *
 * `PlayerVisualProvider.build` is SYNCHRONOUS. The procedural test model can be
 * synchronous because it is lofted from bones already in memory; the master is
 * ~800 kB of GLB plus a 1.8 MB atlas over the network. The three ways to
 * reconcile that are: block (impossible on the main thread), fake it (return an
 * empty geometry now and swap it later, which is the "renders wrong instead of
 * failing" class of bug this whole seam exists to prevent), or ADD an explicit
 * async phase. This is the third.
 *
 * So `preloadAuthoredMaster()` fetches, parses, verifies and caches, and only
 * then does `AUTHORED_MASTER_PROVIDER.available` read true. Before it, the
 * provider is unavailable with a reason that names the call to make — which
 * means a caller that forgets gets the loud fallback path that already exists,
 * not a blank player.
 *
 * ── What is NEVER done here ────────────────────────────────────────────────
 *
 * No runtime bone remap. `RUNTIME_RETARGET_ALLOWED` is false and this module
 * asserts it at load rather than merely honouring it, because "we would never"
 * is not a check. The `M_*.glb` pack is already retargeted OFFLINE onto this
 * rig; a per-frame remap would turn a loud "this clip does not fit this
 * skeleton" into a quiet "this character animates slightly wrong".
 */
import {
  AnimationClip,
  Bone,
  BufferGeometry,
  KeyframeTrack,
  Material,
  Object3D,
  Skeleton,
  SkinnedMesh,
  Texture,
  VectorKeyframeTrack,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { PELVIS_GROUND_LOCK } from './pelvisGroundLock';
import {
  AUTHORED_CLIP_KEY,
  AUTHORED_MASTER_FORBIDDEN,
  AUTHORED_MASTER_REQUIREMENTS,
  RUNTIME_RETARGET_ALLOWED,
} from './authoredMasterSeam';
import {
  AUTHORED_CONTRACT_BONE_COUNT,
  AUTHORED_CONTRACT_MAX_INFLUENCES,
  AUTHORED_CONTRACT_ROOT_BONE,
  AUTHORED_FROZEN_BONE_BY_NAME,
  AUTHORED_SKELETON_CONTRACT_VERSION,
} from './authoredSkeletonContract';
import {
  PRODUCTION_ATLAS,
  PRODUCTION_MESHES,
  PRODUCTION_PRIMARY_LOD,
  productionMesh,
  type ProductionMeshAsset,
} from './productionAssetManifest';
import {
  ProductionAssetIntegrityError,
  assertAtlasBound,
  checkFile,
  type AssetIntegrityFault,
} from './productionAssetIntegrity';

// ── the LOD set ─────────────────────────────────────────────────────────────

export type AuthoredMasterLodLevel = 0 | 1 | 2 | 3;

export const AUTHORED_MASTER_LOD_LEVELS: readonly AuthoredMasterLodLevel[] = PRODUCTION_MESHES.map(
  (m) => m.lod as AuthoredMasterLodLevel,
);

/**
 * The gameplay LOD. The camera sits at 17-20 m and LOD1 covers 6-18 m, so this
 * is the tier a squad benchmark should quote unless it is deliberately
 * measuring another one.
 */
export const AUTHORED_MASTER_PRIMARY_LOD = PRODUCTION_PRIMARY_LOD as AuthoredMasterLodLevel;

/** One loaded, verified tier. Geometry and material are SHARED by every player. */
export interface AuthoredMasterLod {
  readonly level: AuthoredMasterLodLevel;
  /** The URL it was actually fetched from, not the one it was asked for. */
  readonly url: string;
  readonly geometry: BufferGeometry;
  readonly material: Material;
  /** The decoded atlas, or null when texture decoding was skipped. */
  readonly atlas: Texture | null;
  /**
   * Whether the atlas was DECODED and proven to reach the material, as opposed
   * to merely proven deployed. False only on the headless path; see
   * `AuthoredMasterLoadOptions.decodeTextures`.
   */
  readonly atlasDecoded: boolean;
  /**
   * The master's OWN armature, for cloning. Never the Mixamo template: this rig
   * has 74 bones rooted at `root` and shares zero names with that one.
   */
  readonly template: Object3D;
  readonly boneOrder: readonly string[];
  readonly triangles: number;
  /** Measured off the geometry, then cross-checked against the frozen manifest. */
  readonly characterHeight: number;
  readonly soleOffset: number;
}

export interface AuthoredMasterLoadOptions {
  /** Which tiers to load. Defaults to all four. */
  readonly levels?: readonly AuthoredMasterLodLevel[];
  /**
   * Byte source. Defaults to the platform `fetch`, which is what the browser
   * wants; a Node benchmark or a vitest run injects a filesystem reader. It is
   * an injection point rather than an environment sniff so the failure paths are
   * testable without a server.
   */
  readonly fetchBytes?: (url: string) => Promise<ArrayBuffer>;
  /**
   * Decode the atlas PNG and prove it reaches the material.
   *
   * Defaults to true in any environment that can decode an image. Bare Node has
   * neither `createImageBitmap` nor a DOM canvas, so three's image path throws
   * before a bone is read; setting this false registers a texture stub instead.
   *
   * ⚠ IT DOES NOT DROP THE ATLAS CHECK, it swaps which half runs. With decoding
   * on, `assertAtlasBound` proves the pixels reached the draw call. With it off,
   * the atlas FILE is fetched and hash-checked against the frozen manifest, and
   * `atlasDecoded` is reported false so nothing can claim the stronger result.
   */
  readonly decodeTextures?: boolean;
  /**
   * Verify each fetched file's length and SHA-256 against the frozen manifest.
   * On by default. A dev server answering 200 with `index.html` for a missing
   * GLB is the case this catches, and it is common.
   */
  readonly verifyHashes?: boolean;
}

export interface AuthoredMasterPack {
  readonly lods: ReadonlyMap<AuthoredMasterLodLevel, AuthoredMasterLod>;
  readonly atlasDecoded: boolean;
}

// ── refusals ────────────────────────────────────────────────────────────────

export class AuthoredMasterLoadError extends Error {
  constructor(
    readonly url: string,
    readonly problems: readonly string[],
  ) {
    super(
      `the authored master at ${url} does not satisfy the runtime contract:\n` +
        problems.map((p) => `  - ${p}`).join('\n'),
    );
    this.name = 'AuthoredMasterLoadError';
  }
}

const basename = (u: string): string => u.split(/[\\/]/).pop() ?? u;

/**
 * The forbidden list from the seam, ENFORCED rather than documented.
 *
 * Every entry has actually happened to this project or is one edit away, and
 * each one fails in the same expensive way: the model binds, the metrics read
 * healthy, and the player stands in bind pose or animates from the waist down
 * forever.
 */
function refuseForbiddenAsset(url: string): void {
  const name = basename(url);
  const forbidden = AUTHORED_MASTER_FORBIDDEN.find((f) => f.asset === name);
  if (forbidden) {
    throw new AuthoredMasterLoadError(url, [
      `${name} is on the AUTHORED_MASTER_FORBIDDEN list: ${forbidden.why}`,
    ]);
  }
}

/**
 * Everything that must be true of the parsed scene, collected rather than
 * thrown one at a time — an artist who broke three of these wants three lines.
 */
function inspectParsedMaster(scene: Object3D, mesh: ProductionMeshAsset): {
  skinned: SkinnedMesh;
  problems: string[];
} {
  const problems: string[] = [];

  const skinnedMeshes: SkinnedMesh[] = [];
  let meshCount = 0;
  scene.traverse((o) => {
    if ((o as SkinnedMesh).isMesh) meshCount++;
    if ((o as SkinnedMesh).isSkinnedMesh) skinnedMeshes.push(o as SkinnedMesh);
  });

  if (meshCount !== AUTHORED_MASTER_REQUIREMENTS.meshCount) {
    problems.push(
      `${meshCount} meshes; the contract is exactly ${AUTHORED_MASTER_REQUIREMENTS.meshCount} ` +
        `merged SkinnedMesh. 22 players x N draws is the performance cliff the one-mesh ` +
        `clause exists for.`,
    );
  }
  if (skinnedMeshes.length !== 1) {
    problems.push(`${skinnedMeshes.length} SkinnedMeshes; the contract is exactly 1`);
  }
  const skinned = skinnedMeshes[0];
  if (!skinned) {
    throw new AuthoredMasterLoadError(mesh.url, [
      ...problems,
      'no SkinnedMesh at all — this is not a rigged character',
    ]);
  }

  if (Array.isArray(skinned.material)) {
    problems.push(
      `mesh carries ${skinned.material.length} materials; the contract is one shared material`,
    );
  }
  const groups = skinned.geometry?.groups ?? [];
  if (groups.length > 1) {
    problems.push(`geometry is split into ${groups.length} draw groups, i.e. ${groups.length} draws`);
  }

  const skinIndex = skinned.geometry?.getAttribute('skinIndex');
  const skinWeight = skinned.geometry?.getAttribute('skinWeight');
  for (const [label, attr] of [
    ['skinIndex', skinIndex],
    ['skinWeight', skinWeight],
  ] as const) {
    if (!attr) {
      problems.push(`geometry has no ${label} attribute — nothing would deform`);
    } else if (attr.itemSize > AUTHORED_CONTRACT_MAX_INFLUENCES) {
      problems.push(
        `${label} itemSize ${attr.itemSize} exceeds the contract's ` +
          `${AUTHORED_CONTRACT_MAX_INFLUENCES} influences`,
      );
    }
  }

  const bones = skinned.skeleton?.bones ?? [];
  if (bones.length !== AUTHORED_CONTRACT_BONE_COUNT) {
    problems.push(
      `${bones.length} joints; the frozen contract is ${AUTHORED_CONTRACT_BONE_COUNT}. ` +
        `The procedural TEST rig has ${65} and shares zero bone names with this one, so a ` +
        `mix-up here resolves no tracks at all.`,
    );
  }
  const root = bones.find((b) => !(b.parent as Bone | null)?.isBone) ?? bones[0];
  if (root && root.name !== AUTHORED_CONTRACT_ROOT_BONE) {
    problems.push(`root joint is "${root.name}", the contract is "${AUTHORED_CONTRACT_ROOT_BONE}"`);
  }

  const index = skinned.geometry?.getIndex();
  const position = skinned.geometry?.getAttribute('position');
  const triangles = index ? index.count / 3 : position ? position.count / 3 : 0;
  if (triangles !== mesh.triangles) {
    problems.push(
      `${triangles} triangles; the frozen manifest says ${mesh.triangles} for LOD${mesh.lod}`,
    );
  }

  return { skinned, problems };
}

// ── loading ─────────────────────────────────────────────────────────────────

const canDecodeImages = (): boolean =>
  typeof createImageBitmap !== 'undefined' || typeof document !== 'undefined';

const defaultFetchBytes = async (url: string): Promise<ArrayBuffer> => {
  const res = await fetch(url);
  if (!res.ok) throw new AuthoredMasterLoadError(url, [`HTTP ${res.status} — file not deployed`]);
  return res.arrayBuffer();
};

function loaderFor(decodeTextures: boolean): GLTFLoader {
  const loader = new GLTFLoader();
  if (!decodeTextures) {
    // Bare Node reaches for `self`/`createImageBitmap` and throws before a
    // single joint is read. Skeleton, skinning and geometry are untouched by
    // this; see `decodeTextures` for what replaces the check it suspends.
    loader.register(() => ({
      name: 'FG_SKIP_TEXTURE_DECODE',
      loadTexture: () => Promise.resolve(new Texture()),
    }));
  }
  return loader;
}

function throwFaults(faults: readonly AssetIntegrityFault[]): void {
  if (faults.length) {
    const err = new ProductionAssetIntegrityError(faults);
    console.error('[player-assets]', err.message);
    throw err;
  }
}

/** The directory a GLB's sibling atlas resolves against. */
const dirOf = (url: string): string => url.slice(0, url.lastIndexOf('/') + 1);

async function loadOneLod(
  level: AuthoredMasterLodLevel,
  opts: Required<Pick<AuthoredMasterLoadOptions, 'fetchBytes' | 'decodeTextures' | 'verifyHashes'>>,
): Promise<AuthoredMasterLod> {
  const asset = productionMesh(level);
  refuseForbiddenAsset(asset.url);

  const bytes = await opts.fetchBytes(asset.url);
  if (opts.verifyHashes) {
    throwFaults(
      await checkFile(
        asset,
        { ok: true, status: 200, bytes },
        { request: 'mesh-request-failed', size: 'mesh-wrong-size', hash: 'mesh-hash-mismatch' },
      ),
    );
  }

  const gltf = await new Promise<{ scene: Object3D; animations: AnimationClip[] }>((ok, no) => {
    loaderFor(opts.decodeTextures).parse(bytes, dirOf(asset.url), (g) => ok(g as never), no);
  });
  gltf.scene.updateMatrixWorld(true);

  const { skinned, problems } = inspectParsedMaster(gltf.scene, asset);

  /**
   * The mesh GLBs carry NO clips, and one that suddenly did would mean an
   * export merged the pack into the mesh — at which point the twelve clips all
   * named "Animation" collapse onto one entry and only one animation ever
   * plays. Refused here rather than discovered in a playtest.
   */
  if (gltf.animations.length) {
    problems.push(
      `the mesh GLB carries ${gltf.animations.length} clips; the merged export must carry ` +
        `none. The clip pack is twelve separate M_*.glb files keyed on the ` +
        `${AUTHORED_CLIP_KEY}, because all twelve are internally named "Animation".`,
    );
  }

  if (problems.length) throw new AuthoredMasterLoadError(asset.url, problems);

  const geometry = skinned.geometry;
  geometry.computeBoundingBox();
  const box = geometry.boundingBox!;
  const characterHeight = box.max.y - box.min.y;
  const soleOffset = box.min.y;

  // The manifest froze both off the POSITION accessor's own bounds at export
  // time. Measuring again and comparing is what makes "the shipped file is the
  // file we validated" a fact rather than a hash of bytes nobody parsed.
  const drift: string[] = [];
  if (Math.abs(characterHeight - asset.heightM) > 1e-3) {
    drift.push(`height ${characterHeight.toFixed(6)} m vs frozen ${asset.heightM} m`);
  }
  if (Math.abs(soleOffset - asset.soleM) > 1e-3) {
    drift.push(`sole ${soleOffset.toFixed(6)} m vs frozen ${asset.soleM} m`);
  }
  if (drift.length) throw new AuthoredMasterLoadError(asset.url, drift);

  const material = (
    Array.isArray(skinned.material) ? skinned.material[0] : skinned.material
  ) as Material;
  const atlas = (material as Material & { map?: Texture | null }).map ?? null;

  if (opts.decodeTextures) {
    // The last gate: the atlas is deployed AND decoded AND reached the object
    // that draws. All three have produced the same white mannequin
    // independently, so all three are checked.
    assertAtlasBound(material, atlas);
  } else {
    // The headless path cannot decode, so it proves the other half instead: the
    // atlas the GLB asks for by relative URI is deployed and is byte-for-byte
    // the file the manifest froze.
    if (asset.atlasUri !== basename(PRODUCTION_ATLAS.url)) {
      throw new AuthoredMasterLoadError(asset.url, [
        `the GLB asks for "${asset.atlasUri}" but the manifest ships ` +
          `"${basename(PRODUCTION_ATLAS.url)}" — the atlas must be a SIBLING by relative URI`,
      ]);
    }
    const atlasBytes = await opts.fetchBytes(PRODUCTION_ATLAS.url);
    throwFaults(
      await checkFile(
        PRODUCTION_ATLAS,
        { ok: true, status: 200, bytes: atlasBytes },
        { request: 'atlas-request-failed', size: 'atlas-wrong-size', hash: 'atlas-hash-mismatch' },
      ),
    );
  }

  const index = geometry.getIndex();
  const position = geometry.getAttribute('position');
  const triangles = index ? index.count / 3 : position.count / 3;

  return {
    level,
    url: asset.url,
    geometry,
    material,
    atlas: opts.decodeTextures ? atlas : null,
    atlasDecoded: opts.decodeTextures,
    template: gltf.scene,
    boneOrder: skinned.skeleton.bones.map((b) => b.name),
    triangles,
    characterHeight,
    soleOffset,
  };
}

// ── the cache the synchronous `build` reads ─────────────────────────────────

const loaded = new Map<AuthoredMasterLodLevel, AuthoredMasterLod>();
let activeLevel: AuthoredMasterLodLevel = AUTHORED_MASTER_PRIMARY_LOD;
let inFlight: Promise<AuthoredMasterPack> | null = null;

/**
 * Fetch, parse, verify and cache the authored master.
 *
 * Idempotent and de-duplicated: two callers racing on startup get one fetch.
 * Until this resolves, `AUTHORED_MASTER_PROVIDER.available` reads false and the
 * resolver takes its existing loud fallback — which is the whole reason this is
 * an explicit phase rather than a promise hidden inside `build`.
 */
export function preloadAuthoredMaster(
  opts: AuthoredMasterLoadOptions = {},
): Promise<AuthoredMasterPack> {
  if ((RUNTIME_RETARGET_ALLOWED as boolean) !== false) {
    // The constant is `false` and must stay false. If someone flips it, this
    // module refuses to load anything rather than quietly becoming the runtime
    // retargeter the seam forbids. The cast is because TypeScript has narrowed
    // it to the literal `false` and would otherwise delete the check — which is
    // precisely the check that has to survive an edit somewhere else.
    throw new Error(
      'RUNTIME_RETARGET_ALLOWED is true. The M_*.glb pack is already retargeted OFFLINE ' +
        'onto this rig; a runtime remap turns a loud "this clip does not fit this skeleton" ' +
        'into a quiet "this character animates slightly wrong". Refusing to load.',
    );
  }

  const levels = opts.levels ?? AUTHORED_MASTER_LOD_LEVELS;
  const decodeTextures = opts.decodeTextures ?? canDecodeImages();
  const resolved = {
    fetchBytes: opts.fetchBytes ?? defaultFetchBytes,
    decodeTextures,
    verifyHashes: opts.verifyHashes ?? true,
  };

  const missing = levels.filter((l) => !loaded.has(l));
  if (missing.length === 0) {
    return Promise.resolve({ lods: new Map(loaded), atlasDecoded: decodeTextures });
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    for (const level of missing) {
      loaded.set(level, await loadOneLod(level, resolved));
    }
    const first = loaded.get(levels[0]!)!;
    console.info(
      `[authored-master] loaded LOD${levels.join('/')} — ` +
        levels.map((l) => `${loaded.get(l)!.triangles}`).join('/') +
        ` tris, ${first.boneOrder.length} joints, root ${first.boneOrder[0]}, ` +
        `${first.characterHeight.toFixed(4)} m, atlasDecoded=${decodeTextures}`,
    );
    return { lods: new Map(loaded), atlasDecoded: decodeTextures };
  })();

  // ⚠ `.finally()` returns a NEW promise that adopts the rejection, and nobody
  // holds it — so a failed preload raised an unhandled rejection on top of the
  // error the caller was already going to see. The `catch` swallows only that
  // derived copy; `inFlight` itself still rejects for the caller.
  const settled = inFlight;
  settled.finally(() => {
    if (inFlight === settled) inFlight = null;
  }).catch(() => {});
  return inFlight;
}

export function isAuthoredMasterLoaded(level: AuthoredMasterLodLevel = activeLevel): boolean {
  return loaded.has(level);
}

/** The cached tier, or a throw naming the call that would have filled it. */
export function authoredMasterLod(
  level: AuthoredMasterLodLevel = activeLevel,
): AuthoredMasterLod {
  const lod = loaded.get(level);
  if (!lod) {
    throw new Error(
      `authored master LOD${level} is not loaded. Call ` +
        `await preloadAuthoredMaster({ levels: [${level}] }) first — the master is a fetched ` +
        `asset and PlayerVisualProvider.build is synchronous, so the load is an explicit ` +
        `phase rather than a promise hidden inside build().`,
    );
  }
  return lod;
}

export function activeAuthoredMasterLod(): AuthoredMasterLodLevel {
  return activeLevel;
}

/** Which tier `AUTHORED_MASTER_PROVIDER.build` returns. */
export function setActiveAuthoredMasterLod(level: AuthoredMasterLodLevel): void {
  activeLevel = level;
}

/** Drop the cache. For tests and for a benchmark sweeping tiers. */
export function disposeAuthoredMaster(): void {
  for (const lod of loaded.values()) {
    lod.geometry.dispose();
    lod.material.dispose();
    lod.atlas?.dispose();
  }
  loaded.clear();
  inFlight = null;
  activeLevel = AUTHORED_MASTER_PRIMARY_LOD;
}

// ── instantiation ───────────────────────────────────────────────────────────

/** One player, bound to its own copy of the master's 74-bone armature. */
export interface AuthoredMasterInstance {
  readonly root: Object3D;
  readonly mesh: SkinnedMesh;
  readonly skeleton: Skeleton;
  readonly rootBone: Bone;
  readonly bones: Bone[];
  readonly boneOrder: string[];
  readonly triangles: number;
}

/**
 * Clone one player off the loaded tier.
 *
 * `SkeletonUtils.clone` copies the node graph and gives the copy its own
 * `Skeleton` while SHARING the geometry and the material — which is exactly the
 * shape a 22-player benchmark needs: 22 skeletons, 22 mixers, one geometry, one
 * material, one atlas, one draw call per player.
 *
 * The skeleton comes from the GLB's own skin, inverse binds included. It is NOT
 * recomputed from the rest pose: a re-export that rebuilt the bind from a posed
 * skeleton moves those matrices and leaves the locals alone, and recomputing
 * here would silently paper over exactly that.
 */
export function instantiateAuthoredMaster(
  level: AuthoredMasterLodLevel = activeLevel,
): AuthoredMasterInstance {
  const lod = authoredMasterLod(level);
  const root = cloneSkinned(lod.template) as Object3D;
  root.updateMatrixWorld(true);

  let mesh: SkinnedMesh | null = null;
  root.traverse((o) => {
    if (!mesh && (o as SkinnedMesh).isSkinnedMesh) mesh = o as SkinnedMesh;
  });
  if (!mesh) throw new Error('the cloned authored master carries no SkinnedMesh');
  const skinned = mesh as SkinnedMesh;

  const bones = skinned.skeleton.bones;
  const rootBone = bones.find((b) => !(b.parent as Bone | null)?.isBone) ?? bones[0]!;

  return {
    root,
    mesh: skinned,
    skeleton: skinned.skeleton,
    rootBone,
    bones,
    boneOrder: bones.map((b) => b.name),
    triangles: lod.triangles,
  };
}

// ── the clip pack, KEYED ON THE FILE STEM ───────────────────────────────────

export const AUTHORED_CLIP_DIR = '/characters/football/mocap';

/**
 * The twelve production clips, BY FILE STEM.
 *
 * ⚠ Every one of them is internally named `"Animation"`. Keying a clip map on
 * `AnimationClip.name` collapses all twelve onto one entry, and the symptom is
 * "only one animation ever plays" — which reads as a controller bug and is not
 * one. `AUTHORED_CLIP_KEY` states the rule; this list and `loadAuthoredClipPack`
 * are the only place it is applied.
 */
/**
 * The twelve the pack has always shipped — now nine, plus three of ours.
 *
 * ⚠ `Idle`, `Walk` and `Run` REPLACED `M_Idle`, `M_Walk` and `M_Run` on
 * 2026-08-14 and those three files are deleted, not merely unlisted. They are
 * keyed by hand on this rig in `tools/anim/author/clips/{idle,walk,run}.py`
 * rather than retargeted off a Mixamo take, and they carry the twist bones the
 * `M_*` pack does not: measured on the shipped bytes, `M_Run` had
 * `lowerarm_twist_l` at a flat 0.0 deg across all 59 keys — the whole forearm's
 * roll dumped on one deform bone, which is the exact defect `build_clip.py`'s
 * twistsAlive gate exists to catch — where the authored `Run` sweeps 21.5 to
 * 68.8 deg smoothly, worst frame-to-frame step 11.7 deg.
 *
 * They carry NO `M_` prefix and do not need one: `toAnimatorNames` strips that
 * prefix, so `M_Walk` and `Walk` both arrive at the animator as `Walk`. The
 * swap is a drop-in at every call site, which is why nothing downstream of the
 * loader had to change.
 *
 * ⚠ THE NINE THAT REMAIN ARE STILL MIXAMO and there is no authored replacement
 * for any of them. Backpedal, Header, Jog, Pass, Penalty, Sprint, both strafes
 * and ThrowIn have no module under `tools/anim/author/clips/`; deleting them
 * would leave those states drawing nothing.
 */
export const AUTHORED_LOCOMOTION_CLIP_STEMS: readonly string[] = [
  'M_Backpedal',
  'M_Header',
  'Idle',
  'M_Jog',
  'M_Pass',
  'M_Penalty',
  'Run',
  'M_Sprint',
  'M_StrafeLeft',
  'M_StrafeRight',
  'M_ThrowIn',
  'Walk',
  // No `M_` prefix because the file has none; `toAnimatorNames` only strips one
  // when it is there. Bound to the LocoState of the same name, not to an
  // AnimCommand — the forward gait ladder owns it. Moved here from
  // AUTHORED_UNBOUND_CLIP_STEMS on 2026-08-10.
  'SlowWalkForward',
  // Running WITH the ball: the same cycle carried ~11 deg lower with the eyes
  // down. Derived from Jog in `tools/anim/retarget/make_dribble.py` because no
  // "run with the ball at your feet" take exists in any library — and it does
  // not need to, since the ball is the sim's job, not the clip's.
  'Dribble',
];

/**
 * THE ACTIONS THAT USED TO DRAW NOTHING.
 *
 * Every one of these is a finished, measured clip that has existed on
 * `anim/keeper-dive-and-idles` since @d198535 and had never been loaded by the
 * game: the shipping line carried the twelve `M_*` and nothing else, so JOCKEY,
 * FATIGUED, FALL, STUMBLE, THROW_IN_HOLD, TACKLE, SLIDE, GET_UP, CELEBRATE and
 * JUMP were all bound to `NOT_YET_AVAILABLE` and degraded to the gait ladder.
 *
 * They carry NO `M_` prefix, which is not cosmetic: `toAnimatorNames` in
 * castPlayerVisual.ts strips that prefix, so a clip named `M_Jump` would arrive
 * at the animator as `Jump` and a clip named `Jump` also arrives as `Jump`.
 * Keeping them unprefixed means the file stem IS the animator's key, with no
 * transformation in between.
 *
 * `SlideTackle` and `FallForward` both END flat and prone, and `GetUpFront`
 * BEGINS flat and prone. That is deliberate and it is what makes the sim's
 * SlideTackle -> SlideRecover -> (GET_UP) chain continuous instead of a snap.
 */
export const AUTHORED_ACTION_CLIP_STEMS: readonly string[] = [
  'Celebrate',
  'FallForward',
  'Fatigued',
  'GetUpFront',
  'JockeyBackward',
  'JockeyForward',
  'JockeyLeft',
  'JockeyRight',
  'Jump',
  'SlideTackle',
  // Retargeted this session from the RealMotion library onto the frozen rig.
  // `BicycleKick` comes from `scissor kick.fbx`, NOT from the file named
  // `Flying Bicycle Kick.fbx` — that one is a floor crunch, and it retargeted
  // perfectly while looking like a man sitting down.
  'BicycleKick',
  'ControlChest',
  'ControlKnee',
  'HeaderStrike',
  'Kick',
  'KickRight',
  'PassSideFoot',
  // The running strike ('Strike Foward Jog.fbx' whole cycle, 2026-08-14):
  // enters from a jog stride and exits into one, so the live gait blends at
  // both ends. Bound to SHOOT; StandardShotRight stays loaded, unbound.
  'StrikeForwardJog',
  'StandardShotRight',
  'StandingTackle',
  'Stumble',
  'ThrowInHold',
];

/**
 * The idle variations. `M_Idle` is the base; these four are what stop a group
 * of players waiting off the ball from reading as a row of mannequins.
 *
 * ⚠ `IdleScan` is the one to be careful with, and the care is about how it is
 * REVIEWED rather than how it is loaded. Its entire performance is a 68 deg
 * head yaw; it moves no bone's POSITION appreciably, so every positional
 * measurement and every automatically-framed contact sheet reports it as a
 * static statue. It is not. See the note in clip_review.py's `pick_frames`.
 */
export const AUTHORED_IDLE_CLIP_STEMS: readonly string[] = [
  'IdleScan',
  'IdleShiftWeight',
  'IdleShortsTug',
  'IdleToeBounce',
];

/**
 * The goalkeeper set, on the 74-bone rig.
 *
 * These names were ALREADY bound in `CLIP_FOR_COMMAND` before this change, and
 * they resolved only on the 65-joint procedural path, out of
 * `gk-animations.glb`. The cast path never loaded them, so turning the cast on
 * without this list would have left every keeper command bound to a clip that
 * is not in the loaded set — a silent `ClipMissing`, not a crash.
 */
export const AUTHORED_GK_CLIP_STEMS: readonly string[] = [
  'GkCatchLow',
  'GkCatchStanding',
  'GkPickupGround',
  'GkPlaceBall',
  'CarryTurn',
  'GkDiveLowLeft',
  'GkDiveLowRight',
  'GkDiveMidLeft',
  'GkDiveMidRight',
  'GkGetUp',
  'GkGetUpRight',
  'GkOverarmThrow',
  'GkPuntLeft',
  'GkPuntRight',
  'GkReady',
  'GkRollout',
  'GkSetStepLeft',
  'GkSetStepRight',
  'GkSmother',
];

/**
 * Loaded and policy-declared, but NOT yet bound to any `AnimCommand`.
 *
 * They are here rather than omitted so that the set the runtime loads and the
 * set `CLIP_POLICY` declares are the SAME set — `buildProductionAnimationSet`
 * raises `missing-clip` for a declared id nothing supplied, and
 * `undeclared-clip-id` for a supplied id nobody declared, so the two lists
 * drifting apart is a load-time rejection either way.
 *
 * `ShortPassRight`, `ThrowInStandard` and `HeaderStanding` are authored
 * alternatives to three of the `M_*` twelve; the four remaining small-step
 * clips fill the gap under Walk that the gait ladder currently covers by
 * slowing the walk's playback rate. Binding any of them is a separate,
 * reviewed decision.
 *
 * ⚠ `SlowWalkForward` IS NOW BOUND and has moved out of this list — not to an
 * `AnimCommand`, but to the `LocoState` of the same name, which the forward
 * gait ladder selects between 0.35 and 1.26 m/s. It had to move because the
 * runtime only loads what is declared here or in the bound list, and the ladder
 * must never name a clip the clip set does not carry. See
 * `GAIT_BANDS_WITH_STROLL` in playerAnimator.ts for the 53.1 % measurement that
 * forced it.
 */
/**
 * THE TOUCHLINE VOCABULARY — five clips that existed and were never built.
 *
 * `tools/anim/author/clips/coach_*.py` has held these since the authoring
 * pipeline was written. Nothing ever ran `build_clip.py` on them, so no GLB
 * existed, so no stem could name one, so the coaches stood in a footballer's
 * ready stance — and the gap read as "the coach has no animations" rather than
 * as "the coach's animations were never compiled". All five pass their gates
 * unmodified; the modules were finished all along.
 *
 * `CoachIdle` is the one that matters most: it is what a man on a touchline
 * does, as opposed to `Idle`, which is what a footballer waiting for a pass
 * does. Staff share the players' gait ladder, so until this existed the referee
 * and both coaches were standing like players.
 */
export const AUTHORED_COACH_CLIP_STEMS: readonly string[] = [
  'CoachIdle',
  'CoachInstruct',
  'CoachAppeal',
  'CoachApplaud',
  'CoachFrustrated',
];

export const AUTHORED_UNBOUND_CLIP_STEMS: readonly string[] = [
  'HeaderStanding',
  'ShortPassRight',
  'SmallStepBackward',
  'SmallStepForward',
  'SmallStepLeft',
  'SmallStepRight',
  'ThrowInStandard',
];

/**
 * NOT LOADED, and each for a stated reason.
 *
 * ⚠ EMPTY SINCE 2026-08-14, AND THE LIST STAYS. It held `Mixamo_Run_Test`,
 * `Val_Jog_F` and `Val_Sprint_F` — a retarget test fixture and two files that
 * each contained TWO animations, one of them 156 channels on 52 joints, i.e. a
 * different rig. They were kept on disk as validation material for a pack that
 * has since been re-authored, `align-clip-skeletons.mjs` REFUSED both Val files
 * as "a different rig, not float noise", and the owner asked for the superseded
 * clips to go. They are deleted.
 *
 * The list itself is load-bearing even while empty: `loadAuthoredClipPack`
 * checks every GLB in the directory against loaded ∪ excluded, so a file
 * appearing in `mocap/` that nobody declared is a fault rather than a shrug.
 * The next clip somebody parks there needs a line here or a line above.
 */
export const AUTHORED_EXCLUDED_CLIP_STEMS: readonly string[] = [];

/**
 * EVERYTHING THE RUNTIME LOADS, in one list.
 *
 * Declared AFTER its four parts rather than before them: a `const` is hoisted
 * into the temporal dead zone but not initialised, so spreading these above
 * their own declarations throws `Cannot access before initialization` at module
 * evaluation — a blank pitch on first load, with a stack trace that points at
 * this file and not at the ordering.
 */
export const AUTHORED_CLIP_STEMS: readonly string[] = [
  ...AUTHORED_LOCOMOTION_CLIP_STEMS,
  ...AUTHORED_ACTION_CLIP_STEMS,
  ...AUTHORED_IDLE_CLIP_STEMS,
  ...AUTHORED_GK_CLIP_STEMS,
  ...AUTHORED_UNBOUND_CLIP_STEMS,
  ...AUTHORED_COACH_CLIP_STEMS,
];

/**
 * The one bone whose translation is real animation: root motion lives on `hips`.
 * Every other bone's translation in this pack is a constant, and constants are
 * the defect below.
 */
export const ROOT_MOTION_BONE = 'hips';

/**
 * The MASTER's rest hip height in metres, and the datum every authored clip is
 * written against.
 *
 * ── WHY THIS NUMBER HAS TO EXIST ──────────────────────────────────────────
 * `stripConstantBoneTranslation` below removes 73 of the 74 absolute
 * translation tracks precisely because glTF translation is ABSOLUTE and would
 * overwrite each cast member's own rest offsets. It deliberately keeps one:
 * `hips`, because that track is root motion and dropping it would delete the
 * clip's vertical travel.
 *
 * But that track is absolute too. It writes the MASTER's hip height onto
 * whoever is wearing the clip, whatever his own legs measure — so a character
 * whose hips rest higher than this is dragged DOWN by the difference and his
 * boots sink into the pitch, and one whose hips rest lower is pushed UP and he
 * hovers. Measured across the shipping cast, rest hip heights run 939.7 mm
 * (arena_nakamura, 1.747 m) to 1065.0 mm (rivals_sorrentino, 1.925 m): a
 * 126 mm spread, every millimetre of which lands in the turf.
 *
 * On the MASTER itself 50 of the 54 clips touch y = 0 to within a millimetre,
 * which is what proves the clips' own ground plane is right and this datum is
 * the fault. `castLoader` measures each character's rest hips against this and
 * `castPlayerVisual` lifts his root by the difference.
 *
 * ⚠ Do NOT "fix" this by scaling `hips.position[1]`. `root` carries a -90 deg
 * rotation about X, so in the hips' own parent space the vertical axis is Z —
 * the master's rest hips are [0, -0.005, 0.988]. Scaling component 1 touches a
 * near-zero horizontal term and changes the render by nothing at all.
 *
 * Measured by `node scripts/anim-hip-datum.mjs`.
 */
export const AUTHORED_MASTER_HIPS_REST_Y = 0.9878;

/** Two keys are "the same value" below this, in metres (0.01 mm). */
const CONSTANT_EPS_M = 1e-5;

/**
 * Strip the constant bone-translation tracks a clip has no business carrying.
 *
 * ── THE DEFECT ────────────────────────────────────────────────────────────
 * All twelve `M_*.glb` clips carry a translation track for all 74 bones, and
 * 73 of them never change value. Those constants are the MASTER rig's rest
 * offsets — measured equal to 0.00043 mm, so this is not a resemblance, it is
 * the same numbers.
 *
 * glTF `translation` is ABSOLUTE, not rest-relative. So on frame one those
 * tracks overwrite each cast member's own bone offsets with the master's, while
 * the mesh keeps its own inverse bind matrices. Every character is posed on the
 * master's skeleton and wearing its own skin: proportions collapse toward the
 * master, and the skin slides against the bones. Measured on the shipped files,
 * 72 bones move per model, up to 78.4 mm on `rivals_sorrentino` (1.92 m, the
 * tallest) and 50.2 mm on `arena_nakamura` (1.75 m, the shortest). The one
 * model at the master's exact height moves ~0 — which is what proves it is the
 * height difference being flattened and not a broken measurement.
 *
 * ⚠ BLENDER CANNOT SEE THIS, so do not "check it in Blender" and conclude it is
 * fine. Blender pose-bone `location` is REST-RELATIVE: the same clip on the
 * same rig looks correct there. It is only visible by reading the GLB's
 * absolute tracks — see `assets/player-master/pipeline/qa/glb_tracks.py`.
 *
 * ── WHY THE FIX IS HERE, AND WHY IT IS NOT A RE-AUTHOR ────────────────────
 * This is the single choke point every clip passes through, so one edit covers
 * all twelve and any clip added later, and it holds for every cast member
 * without a per-character asset. Rewriting the twelve GLBs would instead mean
 * mutating frozen animation assets to fix a consumer-side misreading of them.
 *
 * Nothing is re-authored or re-timed. A track that never changes value carries
 * no animation; its only effect is to overwrite a skeleton. `hips` is kept
 * whatever it does — that is root motion — and so is any translation track that
 * actually moves, so a future clip that genuinely animates a bone's position is
 * unaffected.
 *
 * The MASTER is provably unchanged by this: the constants ARE its rest offsets,
 * so removing them leaves it in exactly the pose it was already in.
 */
export function stripConstantBoneTranslation(clip: AnimationClip): number {
  const kept: KeyframeTrack[] = [];
  let stripped = 0;
  for (const track of clip.tracks) {
    const dot = track.name.lastIndexOf('.');
    const prop = dot >= 0 ? track.name.slice(dot + 1) : '';
    const node = dot >= 0 ? track.name.slice(0, dot) : track.name;
    if (prop !== 'position' || node === ROOT_MOTION_BONE) {
      kept.push(track);
      continue;
    }
    const v = track.values;
    let constant = true;
    for (let i = 3; i < v.length; i += 1) {
      if (Math.abs(v[i]! - v[i % 3]!) > CONSTANT_EPS_M) {
        constant = false;
        break;
      }
    }
    if (constant) {
      stripped += 1;
    } else {
      kept.push(track);
    }
  }
  if (stripped) clip.tracks = kept;
  return stripped;
}

export interface AuthoredClipPackOptions {
  readonly stems?: readonly string[];
  readonly dir?: string;
  readonly fetchBytes?: (url: string) => Promise<ArrayBuffer>;
  readonly decodeTextures?: boolean;
}

export interface AuthoredClipPack {
  /** Keyed on the FILE STEM. Never on `AnimationClip.name`. */
  readonly clips: ReadonlyMap<string, AnimationClip>;
  readonly problems: readonly string[];
}

// ── THE HANDS ───────────────────────────────────────────────────────────────

/**
 * How a hand is held. Three shapes cover the library: a player running, a
 * keeper presenting his palms, and a pair of hands round a ball.
 */
export type HandStyle = 'relaxed' | 'open' | 'grip' | 'fist';

/**
 * Flexion in degrees at the knuckle, middle and distal joints, plus thumb
 * opposition. Tuned against the master and measured with `anim-hand-tune.mjs`;
 * the resulting thumb-to-index-knuckle gap and PIP flexion are in the table in
 * `poseAuthoredHands`'s comment.
 */
const HAND_STYLES: Record<
  HandStyle,
  { mcp: number; pip: number; dip: number; thumb: number; thumbFlex: number }
> = {
  // ⚠ `thumbFlex` is 0 on these three ON PURPOSE, so they are byte-identical to
  // what they produced before it existed. The keeper's ball-in-glove hold is
  // measured at 7-10 mm on `grip`, and a thumb that curls under the ball would
  // break it. Only `fist` opts in — see below.
  relaxed: { mcp: 22, pip: 30, dip: 18, thumb: 11, thumbFlex: 0 },
  open: { mcp: 10, pip: 12, dip: 8, thumb: 6, thumbFlex: 0 },
  grip: { mcp: 42, pip: 55, dip: 28, thumb: 26, thumbFlex: 0 },
  // `fist` — a RUNNER'S hand, added 2026-08-09. `relaxed` is not relaxed
  // enough for a gait: at 30 deg of PIP it measures 42.2 deg of flexion, which
  // leaves the fingers nearly straight and the hand reads as an open, splayed
  // claw. That is invisible to every measurement the locomotion pass ran,
  // because hand TRAVEL was healthy while hand POSE was wrong — M_Sprint moves
  // its hands 764 mm a cycle with the fingers spread the whole way.
  //
  // It matters more than it sounds: the gameplay camera is a close third-person
  // from behind, so at speed the hands are among the fastest-moving things on
  // screen and the eye lands on them.
  //
  // Chosen by measurement, not taste. The three calibrated styles are linear in
  // the pip parameter — measured PIP = pip + 12.2 deg (30 -> 42.2, 55 -> 67.2,
  // 12 -> 24.2) — and thumb gap is linear at -1.335 mm/deg (11 -> 51.4,
  // 26 -> 31.8, 6 -> 58.5).
  //
  // ── DEEPENED 2026-08-10, AND GIVEN THE THUMB A CURL ────────────────────────
  //
  // The owner circled the sprint's left hand: it reads as a mitten. The first
  // diagnosis — "it folds at the knuckle only, the middle and distal phalanges
  // stay straight" — is measurably NOT what was happening, and chasing it would
  // have rewritten a correct piece of code. `scripts/anim-hand-curl.mjs` reports
  // every joint separately (the instruments that existed reported ONE angle per
  // finger, at `*_02_*`, and could not see the MCP or the DIP at all), and at
  // mcp 44 / pip 72 / dip 32 the finger measured 58 / 84 / 39 deg — curled at
  // all three, in the right direction, on both hands, with real skin weight on
  // every one of the 30 finger bones (`scripts/anim-skin-weights.mjs`).
  //
  // What was actually wrong, seen the moment a camera framed the FINGERS rather
  // than the wrist: the THUMB IS DEAD STRAIGHT. `thumb` only ever applied
  // OPPOSITION — a swing across the palm about local Z — and no flexion at all,
  // so the digit swung inward and then stuck straight up out of the fist like a
  // hitchhiker's. A hand with four curled fingers and one rigid spar reads as a
  // mitten from any distance.
  //
  // The flexion axis is MEASURED, the same way the finger axes were: rotate each
  // thumb bone about each of its own local axes, both signs, and keep whichever
  // brings the thumb TIP toward `middle_01` (where a fisted thumb lands).
  // Local X, +35 deg, moves it 100 -> 74 mm on BOTH hands — unmirrored, exactly
  // like the fingers — while local Z is the mirrored opposition already in use
  // (100 -> 66 mm right, and the opposite sign left).
  //
  //   fist before  index 58.2 / 84.2 / 38.9   thumb 15.3 / 40.2 / 21.6
  //   fist after   index 74.2 /100.2 / 58.9   thumb 24.9 / 54.6 / 48.6
  //   thumb tip -> middle knuckle  53.9 -> 31.6 mm
  //   fingertip -> thumb base      65-80 -> 48-71 mm (rest 135-161)
  //
  // The fingers were deepened with it: 58/84/39 is a cupped claw rather than a
  // fist, and the fingertips stopped 100 mm short of the wrist.
  fist: { mcp: 60, pip: 88, dip: 52, thumb: 30, thumbFlex: 34 },
};

/**
 * Clips whose hands are doing a JOB. Everything not named here is `relaxed`.
 *
 * ⚠ SCOPE, deliberately narrow. `fist` is applied ONLY to the outfield gait
 * clips, one stem at a time, rather than by changing what `relaxed` means.
 * `relaxed` is the default for ~34 unlisted clips, and the keeper's `grip` was
 * measured to a 7-10 mm hold on the ball by a separate agent; widening the
 * default would have reached both. If a gait is added, add it here explicitly.
 */
const CLIP_HAND_STYLE: ReadonlyMap<string, HandStyle> = new Map<string, HandStyle>([
  // Hands round the ball.
  ['ThrowInHold', 'grip'], ['ThrowInStandard', 'grip'], ['M_ThrowIn', 'grip'],
  ['GkRollout', 'grip'], ['GkOverarmThrow', 'grip'], ['GkSmother', 'grip'],
  ['GkCatchLow', 'grip'], ['GkCatchStanding', 'grip'], ['GkPickupGround', 'grip'], ['GkPlaceBall', 'grip'],
  // Palms presented, or bracing on the ground.
  ['GkReady', 'open'], ['GkSetStepLeft', 'open'], ['GkSetStepRight', 'open'],
  ['GkDiveLowLeft', 'open'], ['GkDiveLowRight', 'open'],
  ['GkDiveMidLeft', 'open'], ['GkDiveMidRight', 'open'],
  ['GkGetUp', 'open'], ['GkGetUpRight', 'open'],
  ['FallForward', 'open'], ['GetUpFront', 'open'], ['Celebrate', 'open'],
  // Outfield gaits — a runner carries loose fists, not open palms.
  ['M_Walk', 'fist'], ['M_Jog', 'fist'], ['M_Run', 'fist'], ['M_Sprint', 'fist'],
  ['M_Backpedal', 'fist'], ['M_StrafeLeft', 'fist'], ['M_StrafeRight', 'fist'],
  // ⚠ EIGHTH, and it is not optional. `SlowWalkForward` became the LocoState
  // under Walk on 2026-08-10 and now owns the band the game spends over half
  // its walking in, crossfading directly to and from `M_Walk` at 1.26 m/s.
  // Left off this list it would have been the one gait still on `relaxed` —
  // open, splayed fingers — so the hands would visibly POP open and shut every
  // time a player crossed that edge. Seen in the side render before it shipped.
  ['SlowWalkForward', 'fist'],
]);

const FINGER_NAMES = ['index', 'middle', 'ring', 'pinky'] as const;

/**
 * Rotate `rest` about a local axis. glTF/three order is xyzw throughout, which
 * is also the order the frozen contract stores.
 */
function localFlex(
  rest: readonly [number, number, number, number],
  axis: 'x' | 'z',
  deg: number,
): [number, number, number, number] {
  const h = (deg * Math.PI) / 360;
  const s = Math.sin(h);
  const d: [number, number, number, number] =
    axis === 'x' ? [s, 0, 0, Math.cos(h)] : [0, 0, s, Math.cos(h)];
  const [ax, ay, az, aw] = rest;
  const [bx, by, bz, bw] = d;
  return [
    aw * bx + ax * bw + ay * bz - az * by,
    aw * by - ax * bz + ay * bw + az * bx,
    aw * bz + ax * by - ay * bx + az * bw,
    aw * bw - ax * bx - ay * by - az * bz,
  ];
}

/**
 * REPLACE THE BAKED HAND. Returns the number of finger tracks rewritten.
 *
 * ── THE DEFECT ────────────────────────────────────────────────────────────
 * `tools/anim/retarget/retarget_reference.json` gives every PROXIMAL finger
 * bone a 62.0 deg `targetReferenceLocalQuat` — thumb_01, index_01, middle_01,
 * ring_01, pinky_01, both hands — and leaves every _02_ and _03_ phalanx at
 * identity. `rig.py` applies that reference, captures `ref_world` from the
 * POSED armature, and then pins every bone a clip does not name — which is
 * every finger — to `ref_world`. So all seventeen Blender-authored clips bake
 * the Mixamo retarget reference as their resting hand.
 *
 * 62 deg at the knuckle with the two phalanges above it dead straight is a
 * rake, not a hand — `rig.py`'s own `_grip` docstring names that shape — and on
 * the thumb that axis swings it AWAY from the index. Measured over the library:
 *
 *   authored clips (17)   thumb gap 126 mm, PIP flexion 12-17 deg
 *   retargeted clips (37) thumb gap 27-140 mm, PIP flexion 12-77 deg
 *   the master at rest    thumb gap  67 mm, PIP flexion  7-12 deg (flat hand)
 *
 * `grip: {"thumb": 0.0}` never moved it for two independent reasons: `_grip`
 * reads `grip.get(s)` for `s in "lr"`, so `"thumb"` is not a key it looks at,
 * and `if not curl: continue` returns before touching a bone anyway.
 *
 * ── WHY HERE AND NOT IN THE CLIPS ─────────────────────────────────────────
 * Same reasoning as `stripConstantBoneTranslation` directly above: this is the
 * one choke point every clip passes through, so one edit covers all 54 and
 * anything added later, without mutating frozen animation assets. It also
 * covers the retargeted clips, which have their own bad hands from a different
 * cause and could not be fixed by re-authoring at all.
 *
 * The rest pose comes from the frozen contract, so no new asset is involved and
 * a rig change moves it automatically.
 *
 * ⚠ The flexion axes are MEASURED, not derived — `scripts/anim-hand-calibrate.mjs`
 * rotates each phalanx about each of its own local axes, both signs, and keeps
 * whichever moves the fingertip toward the wrist. `rig.py` carries a long
 * comment about a chirality derivation that was secretly a constant and pried
 * both hands open; the fix for that is a measurement, not a better cross
 * product. Fingers flex about local X on both hands; the thumb opposes about
 * local Z with the sign mirrored between hands.
 *
 * Resulting measurements, both hands, off the master:
 *
 *   relaxed  thumb gap 51.4 mm, index PIP 42.2 deg
 *   open     thumb gap 58.5 mm, index PIP 24.2 deg
 *   grip     thumb gap 31.8 mm, index PIP 67.2 deg
 *
 * The pose is CONSTANT over the clip. Finger animation in this library is
 * either baked-constant already (the authored 17) or mocap noise no camera in
 * this game can resolve, and a correct silhouette beats a wrong one that moves.
 */
export function poseAuthoredHands(clip: AnimationClip, style: HandStyle): number {
  const s = HAND_STYLES[style];
  const target = new Map<string, [number, number, number, number]>();

  for (const side of ['l', 'r'] as const) {
    const degs = [s.mcp, s.pip, s.dip];
    for (const finger of FINGER_NAMES) {
      for (let j = 0; j < 3; j++) {
        const bone = `${finger}_0${j + 1}_${side}`;
        const rest = AUTHORED_FROZEN_BONE_BY_NAME.get(bone)?.quat;
        if (rest) target.set(bone, localFlex(rest, 'x', degs[j]!));
      }
    }
    // The thumb OPPOSES about the palm normal rather than curling toward the
    // wrist — that is what makes a grip read as a grip — and the two hands are
    // mirrored, so the sign flips.
    const sign = side === 'r' ? 1 : -1;
    const tdegs = [s.thumb, s.thumb * 0.6, s.thumb * 0.35];
    // ⚠ ...AND THEN IT CURLS, which opposition alone never did. Applied AFTER
    // the opposition, so the curl happens in the swung frame, which is the order
    // the joints work in. The axis is local X and it is NOT mirrored — measured,
    // see the table in HAND_STYLES. `thumbFlex: 0` reproduces the previous
    // behaviour exactly, which is how `grip` and `relaxed` are held still.
    const fdegs = [s.thumbFlex * 0.5, s.thumbFlex, s.thumbFlex * 0.9];
    for (let j = 0; j < 3; j++) {
      const bone = `thumb_0${j + 1}_${side}`;
      const rest = AUTHORED_FROZEN_BONE_BY_NAME.get(bone)?.quat;
      if (!rest) continue;
      const opposed = localFlex(rest, 'z', sign * tdegs[j]!);
      target.set(bone, s.thumbFlex ? localFlex(opposed, 'x', fdegs[j]!) : opposed);
    }
  }

  let rewritten = 0;
  for (const track of clip.tracks) {
    const dot = track.name.lastIndexOf('.');
    if (dot < 0) continue;
    if (track.name.slice(dot + 1) !== 'quaternion') continue;
    const q = target.get(track.name.slice(0, dot));
    if (!q) continue;
    const v = track.values;
    for (let i = 0; i + 3 < v.length; i += 4) {
      v[i] = q[0]; v[i + 1] = q[1]; v[i + 2] = q[2]; v[i + 3] = q[3];
    }
    rewritten += 1;
  }
  return rewritten;
}

/** The style this clip's hands should hold. `relaxed` unless named otherwise. */
export function handStyleFor(stem: string): HandStyle {
  return CLIP_HAND_STYLE.get(stem) ?? 'relaxed';
}

// ── THE PELVIS ──────────────────────────────────────────────────────────────

/**
 * ⚠ THE HIPS' OWN VERTICAL AXIS IS Z, NOT Y.
 *
 * `root` carries a -90 deg rotation about X (a Z-up source converted to Y-up)
 * and `hips` is its direct child, so in the hips' parent space the vertical
 * component of `translation` is index 2. The master's rest hips are
 * [0, -0.005, 0.988]. Writing index 1 touches a near-zero horizontal term and
 * changes the render by NOTHING while looking exactly like a working fix.
 */
const HIPS_VERTICAL_COMPONENT = 2;

/** Sample a 3-component keyframe track at time `t` (linear, clamped). */
function sampleVec3(times: ArrayLike<number>, values: ArrayLike<number>, t: number): [number, number, number] {
  const n = times.length;
  if (n === 0) return [0, 0, 0];
  if (t <= times[0]!) return [values[0]!, values[1]!, values[2]!];
  const last = n - 1;
  if (t >= times[last]!) return [values[last * 3]!, values[last * 3 + 1]!, values[last * 3 + 2]!];
  let i = 0;
  while (i < last && times[i + 1]! < t) i++;
  const t0 = times[i]!, t1 = times[i + 1]!;
  const u = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
  const a = i * 3, b = (i + 1) * 3;
  return [
    values[a]! + (values[b]! - values[a]!) * u,
    values[a + 1]! + (values[b + 1]! - values[a + 1]!) * u,
    values[a + 2]! + (values[b + 2]! - values[a + 2]!) * u,
  ];
}

/**
 * GIVE THE PELVIS BACK ITS VERTICAL TRAVEL. Returns true if the clip was
 * corrected.
 *
 * ── THE DEFECT ────────────────────────────────────────────────────────────
 * Six clips ship a `hips` translation track with TWO keys that never change —
 * no pelvis travel at all — against 17 to 37 keys and 41-75 mm of travel in
 * every other gait. Their legs cycle beneath a pelvis pinned at one height, so
 * the stance leg has nowhere to go but through the pitch. Measured on the
 * MASTER, where the hip datum cannot be blamed: M_Run -61.0 mm, M_Penalty
 * -34.7, M_Pass -13.4, M_Header -11.1, M_ThrowIn -9.2. That list is almost
 * exactly the list of clips that penetrate — one defect, not five.
 *
 * M_Run is worse than penetration alone. Its two stances bottom at +35 mm and
 * -61 mm: one foot hovers while the other ploughs, a 96 mm asymmetry that
 * reads as a limp on the clip every outfield player spends most of the match
 * in.
 *
 * ── THE CORRECTION ────────────────────────────────────────────────────────
 * Measured off the clip's own foot contact — the lowest SKINNED vertex per
 * frame on the master — never authored by hand. Two modes, and the distinction
 * matters:
 *
 *   stance-lock       interpolates the lift between successive STANCE MINIMA,
 *                     so both stances land at zero. Because a curve through
 *                     alternating stance heights IS the pelvis bob, this
 *                     removes the limp and restores the missing travel in one
 *                     operation: M_Run gains 95 mm peak-to-peak, in range with
 *                     the 41-75 mm the other gaits carry.
 *
 *   penetration-only  lift = max(0, -y). Used for everything acyclic, because
 *                     a foot off the ground during a kick follow-through or a
 *                     dive is MEANT to be off the ground and forcing it down
 *                     would invent contact the animator never authored.
 *
 * Clips already on the ground are not touched at all — M_Walk (-0.2 mm),
 * M_Jog (-1.6) and M_Sprint (-7.1) are correct as authored and a correction
 * could only make them worse.
 *
 * ⚠ THIS DOES NOT RETIME ANYTHING. Only the vertical component of the pelvis
 * moves; no key moves in time, no rotation is touched, and the strike contact
 * frame at 0.578 of the clip is exactly where it was. The sim's shot impulse
 * window (0.52-0.66) is unaffected.
 */
export function applyPelvisGroundLock(clip: AnimationClip, stem: string): boolean {
  const lock = PELVIS_GROUND_LOCK.get(stem);
  if (!lock) return false;
  const track = clip.tracks.find((t) => t.name === `${ROOT_MOTION_BONE}.position`);
  if (!track) return false;

  const n = lock.lift.length;
  const times = new Float32Array(n);
  const values = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const t = (i / n) * clip.duration;
    times[i] = t;
    const base = sampleVec3(track.times, track.values, t);
    values[i * 3] = base[0];
    values[i * 3 + 1] = base[1];
    values[i * 3 + 2] = base[2];
    values[i * 3 + HIPS_VERTICAL_COMPONENT] =
      (values[i * 3 + HIPS_VERTICAL_COMPONENT] ?? 0) + (lock.lift[i] ?? 0);
  }
  const replacement = new VectorKeyframeTrack(track.name, Array.from(times), Array.from(values));
  clip.tracks[clip.tracks.indexOf(track)] = replacement;
  return true;
}

/**
 * Load the clip pack and key it on the file stem.
 *
 * Each clip is renamed to its stem as it is admitted, so anything downstream
 * that does reach for `clip.name` — a mixer action lookup, a debug overlay —
 * gets the twelve distinct names rather than twelve copies of "Animation".
 * A file carrying anything other than exactly one clip is reported, not merged.
 */
export async function loadAuthoredClipPack(
  opts: AuthoredClipPackOptions = {},
): Promise<AuthoredClipPack> {
  const stems = opts.stems ?? AUTHORED_CLIP_STEMS;
  const dir = opts.dir ?? AUTHORED_CLIP_DIR;
  const fetchBytes = opts.fetchBytes ?? defaultFetchBytes;
  const decodeTextures = opts.decodeTextures ?? canDecodeImages();

  const clips = new Map<string, AnimationClip>();
  const problems: string[] = [];

  for (const stem of stems) {
    const url = `${dir}/${stem}.glb`;
    refuseForbiddenAsset(url);
    let animations: AnimationClip[];
    try {
      const bytes = await fetchBytes(url);
      const gltf = await new Promise<{ animations: AnimationClip[] }>((ok, no) => {
        loaderFor(decodeTextures).parse(bytes, `${dir}/`, (g) => ok(g as never), no);
      });
      animations = gltf.animations;
    } catch (e) {
      problems.push(`${stem}: ${e instanceof Error ? e.message : String(e)}`);
      continue;
    }
    if (animations.length !== 1) {
      problems.push(
        `${stem}: carries ${animations.length} clips; the pack is exactly one clip per file`,
      );
      continue;
    }
    if (clips.has(stem)) {
      problems.push(`${stem}: duplicate stem — refusing to overwrite the clip already admitted`);
      continue;
    }
    const clip = animations[0]!;
    clip.name = stem;
    // See stripConstantBoneTranslation: without this every cast member is posed
    // on the MASTER's skeleton and loses its own height and proportions.
    stripConstantBoneTranslation(clip);
    // See poseAuthoredHands: every clip in this pack ships a hand posed by the
    // Mixamo retarget reference — a 62 deg rake with the thumb splayed 126 mm
    // off the index knuckle against 67 mm at rest.
    poseAuthoredHands(clip, handStyleFor(stem));
    // See applyPelvisGroundLock: six clips carry a pelvis pinned at one height,
    // so the stance leg drives the boot through the pitch. Vertical only — no
    // key moves in time, so no strike contact frame moves.
    applyPelvisGroundLock(clip, stem);
    clips.set(stem, clip);
  }

  return { clips, problems };
}

/** The throwing form. A partial pack is a bind-pose statue on some commands. */
export async function assertAuthoredClipPack(
  opts: AuthoredClipPackOptions = {},
): Promise<ReadonlyMap<string, AnimationClip>> {
  const pack = await loadAuthoredClipPack(opts);
  const want = opts.stems ?? AUTHORED_CLIP_STEMS;
  const missing = want.filter((s) => !pack.clips.has(s));
  if (pack.problems.length || missing.length) {
    throw new Error(
      `the authored clip pack is incomplete:\n` +
        [...pack.problems, ...missing.map((m) => `${m}: missing`)]
          .map((p) => `  - ${p}`)
          .join('\n'),
    );
  }
  return pack.clips;
}

/** The version every loaded master asset declares. Re-exported for callers. */
export { AUTHORED_SKELETON_CONTRACT_VERSION };
